import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ImpactFilters, ImpactKPI, MaterialSummary, UnitRanking } from './useImpactData';

interface ExportData {
  totalWeight: number;
  materialSummary: MaterialSummary[];
  kpis: ImpactKPI[];
  unitRanking: UnitRanking[];
}

interface ExportOptions {
  filters: ImpactFilters;
  data: ExportData;
}

export const useImpactPdfExport = () => {
  const exportToPdf = ({ filters, data }: ExportOptions) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(34, 139, 34); // Forest green
    doc.text('Relatório de Impacto Ambiental', pageWidth / 2, 20, { align: 'center' });
    
    // Date range and filters info
    doc.setFontSize(10);
    doc.setTextColor(100);
    const dateRange = `Período: ${format(filters.dateRange.from, 'dd/MM/yyyy', { locale: ptBR })} a ${format(filters.dateRange.to, 'dd/MM/yyyy', { locale: ptBR })}`;
    doc.text(dateRange, pageWidth / 2, 30, { align: 'center' });
    
    // Generated date
    doc.setFontSize(8);
    doc.text(`Gerado em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}`, pageWidth / 2, 36, { align: 'center' });
    
    let yPos = 48;

    // Active Filters Section
    const activeFilters: string[] = [];
    if (filters.establishments.length > 0) {
      activeFilters.push(`Unidades: ${filters.establishments.length} selecionada(s)`);
    }
    if (filters.destinationType !== 'all') {
      activeFilters.push(`Tipo de destino: ${filters.destinationType}`);
    }
    
    if (activeFilters.length > 0) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text(`Filtros aplicados: ${activeFilters.join(' | ')}`, 14, yPos);
      yPos += 10;
    }

    // Total Weight Section
    doc.setFontSize(14);
    doc.setTextColor(34, 139, 34);
    doc.text('Resumo Geral', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(`Peso Total Coletado: ${formatNumber(data.totalWeight)} kg`, 14, yPos);
    yPos += 12;

    // KPIs Table
    doc.setFontSize(14);
    doc.setTextColor(34, 139, 34);
    doc.text('Indicadores de Impacto', 14, yPos);
    yPos += 6;

    const kpiTableData = data.kpis.map(kpi => [
      kpi.label,
      `${formatNumber(kpi.value)} ${kpi.unit}`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Indicador', 'Valor']],
      body: kpiTableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [34, 139, 34],
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: { fontSize: 10 },
      margin: { left: 14, right: 14 }
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Material Summary Table
    if (data.materialSummary.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(34, 139, 34);
      doc.text('Distribuição por Tipo de Material', 14, yPos);
      yPos += 6;

      const materialTableData = data.materialSummary.map(m => [
        m.material,
        `${formatNumber(m.weight)} kg`,
        `${m.percentage.toFixed(1)}%`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Material', 'Peso', 'Percentual']],
        body: materialTableData,
        theme: 'striped',
        headStyles: { 
          fillColor: [34, 139, 34],
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: { fontSize: 10 },
        margin: { left: 14, right: 14 }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    // Check if we need a new page
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    // Unit Ranking Table
    if (data.unitRanking.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(34, 139, 34);
      doc.text('Ranking por Unidade', 14, yPos);
      yPos += 6;

      const unitTableData = data.unitRanking.map((u, index) => [
        `${index + 1}º`,
        u.unit,
        `${formatNumber(u.value)} kg`,
        `${formatNumber(u.co2)} kg CO₂`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Posição', 'Unidade', 'Peso Total', 'CO₂ Evitado']],
        body: unitTableData,
        theme: 'striped',
        headStyles: { 
          fillColor: [34, 139, 34],
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 80 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35 }
        },
        margin: { left: 14, right: 14 }
      });
    }

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save
    const fileName = `relatorio_impacto_${format(new Date(), 'yyyy-MM-dd_HHmm')}.pdf`;
    doc.save(fileName);
  };

  return { exportToPdf };
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2
  }).format(value);
}
